// src/components/dashboard/SystemParameterManager.jsx
import React, { useMemo, useState } from 'react';
import { COLORS, PARAM_CATEGORIES } from '../../constants';
import { DashboardSection, PAGE } from '../ui';
import { findHeader, isPassValue, isFailValue } from '../../utils/helpers';
import {
  isDoaAuditRow,
  isValidDoaParameterValue,
} from '../../utils/processors';
import Donut from './Donut';
import GroupCard from './GroupCard';

// SVG Icons
const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TrendingDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="12" x2="12" y2="16" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const DOAIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export default function SystemParameterManager({ doaData }) {
  const [selectedParam, setSelectedParam] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const source = doaData && doaData.length > 0 ? doaData : [];

  const headers =
    source.length > 0
      ? Object.keys(source[0]).filter((h) => String(h).trim() !== '')
      : [];

  const groupCol = useMemo(
    () =>
      findHeader(headers, [
        /description/i,
        /product\s*id/i,
        /product\s*code/i,
        /asset\s*type/i,
        /equipment\s*type/i,
        /device\s*type/i,
        /category/i,
        /product/i,
        /equipment\s*group/i,
        /device\s*group/i,
        /equipment\s*name/i,
        /device\s*name/i,
        /item\s*name/i,
        /asset\s*name/i,
        /name/i,
      ]) ||
      headers[0] ||
      '',
    [headers]
  );

  const paramColumns = useMemo(() => {
    const columns = {};

    PARAM_CATEGORIES.forEach((param) => {
      columns[param.id] = findHeader(headers, [param.columnPattern]);
    });

    return columns;
  }, [headers]);

  const paramStats = useMemo(() => {
    const result = {};

    PARAM_CATEGORIES.forEach((param) => {
      const valueCol = paramColumns[param.id];
      const groups = new Map();

      source.forEach((row) => {
        if (!valueCol) return;
        if (isDoaAuditRow(row)) return;

        const value = row[valueCol];

        if (!isValidDoaParameterValue(value)) return;

        const rawGroup = groupCol ? String(row[groupCol] || '').trim() : '';

        const groupName =
          rawGroup && rawGroup.toLowerCase() !== 'unknown' && rawGroup !== '-'
            ? rawGroup
            : '(Unknown)';

        if (!groups.has(groupName)) {
          groups.set(groupName, {
            name: groupName,
            passed: 0,
            total: 0,
            doaCount: 0,
          });
        }

        const target = groups.get(groupName);

        target.total += 1;

        if (isPassValue(value) && !isFailValue(value)) {
          target.passed += 1;
        }

        if (isFailValue(value)) {
          target.doaCount += 1;
        }
      });

      const groupList = Array.from(groups.values()).sort(
        (a, b) => b.total - a.total || a.name.localeCompare(b.name)
      );

      const totalPassed = groupList.reduce(
        (acc, group) => acc + group.passed,
        0
      );

      const totalScanned = groupList.reduce(
        (acc, group) => acc + group.total,
        0
      );

      const totalDoa = groupList.reduce(
        (acc, group) => acc + group.doaCount,
        0
      );

      result[param.id] = {
        groups: groupList,
        totalPassed,
        totalScanned,
        totalDoa,
        overallPct:
          totalScanned > 0
            ? Math.round((totalPassed / totalScanned) * 100)
            : 0,
      };
    });

    return result;
  }, [source, groupCol, paramColumns]);

  const criticalIssues = useMemo(() => {
    const issues = [];
    PARAM_CATEGORIES.forEach((param) => {
      const stats = paramStats[param.id];
      if (stats && stats.totalDoa > 0) {
        issues.push({
          param: param.title,
          doaCount: stats.totalDoa,
          totalScanned: stats.totalScanned,
          doaRate: Math.round((stats.totalDoa / stats.totalScanned) * 100),
          passRate: stats.overallPct,
        });
      }
    });
    return issues.sort((a, b) => b.doaRate - a.doaRate);
  }, [paramStats]);

  const getParameterColor = (passRate, hasData, hasDoa) => {
    if (!hasData) return COLORS.slate;
    if (hasDoa) return COLORS.warning; // Yellow if ANY DOA exists
    if (passRate >= 90) return COLORS.success;
    if (passRate >= 70) return '#10b981';
    if (passRate >= 50) return COLORS.warning;
    if (passRate >= 30) return '#f97316';
    return COLORS.danger;
  };

  const getParameterStatus = (passRate, hasData, hasDoa) => {
    if (!hasData) return 'No Data';
    if (hasDoa) return 'DOA Detected';
    if (passRate >= 90) return 'Excellent';
    if (passRate >= 70) return 'Good';
    if (passRate >= 50) return 'Fair';
    if (passRate >= 30) return 'Poor';
    return 'Critical';
  };

  const totalDoaCount = useMemo(() => {
    return PARAM_CATEGORIES.reduce((sum, param) => {
      const stats = paramStats[param.id];
      return sum + (stats?.totalDoa || 0);
    }, 0);
  }, [paramStats]);

  const totalScannedCount = useMemo(() => {
    return PARAM_CATEGORIES.reduce((sum, param) => {
      const stats = paramStats[param.id];
      return sum + (stats?.totalScanned || 0);
    }, 0);
  }, [paramStats]);

  const overallDoaRate = totalScannedCount > 0 ? Math.round((totalDoaCount / totalScannedCount) * 100) : 0;

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .param-card {
          animation: fadeInUp 0.4s ease-out;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .critical-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>

      <DashboardSection
        title="DOA Monitoring Dashboard"
        subtitle="Dead on Arrival tracking for PSU, Fan, Airflow & LED systems"
      >
        {/* DOA Summary Banner */}
        <div
          style={{
            marginBottom: 24,
            padding: '16px 20px',
            background: totalDoaCount > 0 ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)' : 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
            borderRadius: 12,
            borderLeft: `4px solid ${totalDoaCount > 0 ? COLORS.danger : COLORS.success}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className={totalDoaCount > 0 ? 'critical-pulse' : ''}>
              {totalDoaCount > 0 ? <AlertIcon /> : <CheckIcon />}
            </div>
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: totalDoaCount > 0 ? COLORS.danger : COLORS.success }}>
                {totalDoaCount > 0 ? `${totalDoaCount} DOA Units Detected` : 'No DOA Units Detected'}
              </span>
              <span style={{ fontSize: 12, color: COLORS.textSecondary, marginLeft: 8 }}>
                {overallDoaRate}% DOA rate across {totalScannedCount} scanned units
              </span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
            <InfoIcon />
          </div>
        </div>

        {/* Critical Issues Banner - Specific DOA Alerts */}
        {criticalIssues.length > 0 && (
          <div
            style={{
              marginBottom: 24,
              padding: '12px 16px',
              background: COLORS.warningLight,
              borderRadius: 10,
              borderLeft: `3px solid ${COLORS.warning}`,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <DOAIcon />
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.warning }}>DOA Alert:</span>
            <span style={{ fontSize: 11, color: COLORS.textSecondary }}>
              {criticalIssues.map(issue => `${issue.param} (${issue.doaCount} DOA, ${issue.doaRate}% failure)`).join(' • ')}
            </span>
          </div>
        )}

        {/* Radial Progress Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 20,
            marginBottom: selectedParam ? 24 : 0,
          }}
        >
          {PARAM_CATEGORIES.map((param, idx) => {
            const stats = paramStats[param.id];
            const passRate = stats?.overallPct || 0;
            const hasData = stats?.totalScanned > 0;
            const hasDoa = stats?.totalDoa > 0;
            const doaCount = stats?.totalDoa || 0;
            const color = getParameterColor(passRate, hasData, hasDoa);
            const status = getParameterStatus(passRate, hasData, hasDoa);
            const isSelected = selectedParam === param.id;
            const isHovered = hoveredCard === param.id;
            
            return (
              <div
                key={param.id}
                className="param-card"
                style={{
                  textAlign: 'center',
                  padding: '20px 16px',
                  background: isSelected ? COLORS.surface : 'white',
                  borderRadius: 16,
                  border: `2px solid ${isSelected ? color : COLORS.line}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
                  boxShadow: isHovered ? '0 12px 28px rgba(0,0,0,0.1)' : 'none',
                }}
                onMouseEnter={() => setHoveredCard(param.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setSelectedParam(isSelected ? null : param.id)}
              >
                {/* Radial Progress Ring */}
                <div style={{ position: 'relative', width: 110, height: 110, margin: '0 auto' }}>
                  <svg width="110" height="110" viewBox="0 0 110 110">
                    {/* Background circle */}
                    <circle
                      cx="55"
                      cy="55"
                      r="46"
                      fill="none"
                      stroke={COLORS.line}
                      strokeWidth="8"
                    />
                    {/* Progress circle - shows PASS rate */}
                    <circle
                      cx="55"
                      cy="55"
                      r="46"
                      fill="none"
                      stroke={color}
                      strokeWidth="8"
                      strokeDasharray={`${hasData ? (passRate / 100) * 289 : 0} 289`}
                      strokeLinecap="round"
                      transform="rotate(-90 55 55)"
                      style={{ transition: 'stroke-dasharray 0.8s ease' }}
                    />
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 20, fontWeight: 800, color }}>
                      {hasData ? `${passRate}%` : '—'}
                    </div>
                    <div style={{ fontSize: 8, color: COLORS.textSecondary }}>
                      Pass Rate
                    </div>
                  </div>
                </div>

                {/* Title and Status */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                    {param.title}
                  </div>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 6,
                      padding: '3px 10px',
                      borderRadius: 20,
                      background: hasDoa ? `${COLORS.warning}20` : `${color}15`,
                      color: hasDoa ? COLORS.warning : color,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {status}
                    {hasDoa ? <AlertIcon /> : hasData && passRate >= 70 ? <TrendingUpIcon /> : hasData ? <TrendingDownIcon /> : null}
                  </div>
                </div>

                {/* DOA Stats - Highlighted */}
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: `1px solid ${COLORS.line}`,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 16,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.success }}>
                      {stats?.totalPassed || 0}
                    </div>
                    <div style={{ fontSize: 9, color: COLORS.textSecondary }}>Passed</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: hasDoa ? COLORS.warning : COLORS.danger }}>
                      {doaCount}
                    </div>
                    <div style={{ fontSize: 9, color: COLORS.textSecondary }}>DOA</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.textSecondary }}>
                      {stats?.totalScanned || 0}
                    </div>
                    <div style={{ fontSize: 9, color: COLORS.textSecondary }}>Total</div>
                  </div>
                </div>

                {/* Expand Indicator */}
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 10,
                    color: COLORS.primary,
                    opacity: isHovered ? 1 : 0.5,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {isSelected ? '▼ Click to collapse' : '▲ Click to expand'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Expanded Details Section - Shows DOA breakdown by equipment */}
        {selectedParam && (
          <div
            style={{
              marginTop: 24,
              padding: 24,
              background: 'white',
              borderRadius: 16,
              border: `1px solid ${COLORS.line}`,
              animation: 'fadeInUp 0.3s ease-out',
            }}
          >
            {(() => {
              const param = PARAM_CATEGORIES.find(p => p.id === selectedParam);
              const stats = paramStats[selectedParam];
              
              if (!param || !stats) return null;
              
              const hasDoa = stats.totalDoa > 0;
              
              return (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 20,
                      paddingBottom: 12,
                      borderBottom: `2px solid ${COLORS.line}`,
                    }}
                  >
                    <div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                        {param.title} - DOA Breakdown
                      </h3>
                      <p style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4 }}>
                        Dead on Arrival analysis by equipment type
                      </p>
                    </div>
                    <div
                      style={{
                        padding: '6px 14px',
                        borderRadius: 20,
                        background: hasDoa ? `${COLORS.warning}20` : `${COLORS.success}20`,
                        color: hasDoa ? COLORS.warning : COLORS.success,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {hasDoa ? `${stats.totalDoa} DOA Units` : 'No DOA Units'}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {stats.groups.length > 0 ? (
                      stats.groups.map((group) => (
                        <GroupCard
                          key={`${selectedParam}-${group.name}`}
                          name={group.name}
                          passed={group.passed}
                          total={group.total}
                          doaCount={group.doaCount}
                        />
                      ))
                    ) : (
                      <div
                        style={{
                          gridColumn: '1 / -1',
                          textAlign: 'center',
                          padding: 40,
                          color: COLORS.textSecondary,
                        }}
                      >
                        No DOA data available for {param.title}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* DOA Summary Footer */}
        <div
          style={{
            marginTop: 24,
            padding: '12px 20px',
            background: COLORS.surface,
            borderRadius: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            border: `1px solid ${COLORS.line}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 10, background: COLORS.success }} />
              <span style={{ fontSize: 10, color: COLORS.textSecondary }}>No DOA</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 10, background: COLORS.warning }} />
              <span style={{ fontSize: 10, color: COLORS.textSecondary }}>DOA Detected (Yellow)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 10, background: COLORS.danger }} />
              <span style={{ fontSize: 10, color: COLORS.textSecondary }}>Critical DOA Rate</span>
            </div>
          </div>
          <div style={{ fontSize: 10, color: COLORS.textSecondary }}>
            DOA = Dead on Arrival units detected during quality inspection
          </div>
        </div>
      </DashboardSection>
    </>
  );
}